import React, { useState, useEffect } from "react";
import Select from "react-select";
import { getStates, getDistricts } from "../../services/locationApi";

const propertyOptions = [
  { value: "RESIDENTIAL", label: "Residential" },
  { value: "COMMERCIAL", label: "Commercial" },
  { value: "AGRICULTURE", label: "Agriculture" },
  { value: "INDUSTRIAL", label: "Industrial" },
];

const MultiSelectComponent = ({ onSelectionChange }) => {
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      const stateList = await getStates();
      setStates(stateList.map((s) => ({ value: s?.iso2, label: s?.name })));
    };
    fetchStates();
  }, []);

  // Fetch and clean districts when state changes
  useEffect(() => {
    if (selectedState) {
      const fetchDistricts = async () => {
        const districtList = await getDistricts(selectedState?.value);

        // Normalize and clean district names
        let normalizedDistricts = districtList.map((d) => {
          let cleanLabel = d?.name.replace(/\s?[IVXLCDM]+$/, "").trim(); // Remove Roman numerals
          return {
            value: d?.id,
            label: cleanLabel,
            normalized: cleanLabel.toLowerCase(), // For comparison
          };
        });

        // Remove duplicates based on lowercase name
        let uniqueDistricts = Array.from(new Map(normalizedDistricts.map(d => [d.normalized, d])).values());

        setDistricts(uniqueDistricts);
      };
      fetchDistricts();
    } else {
      setDistricts([]);
      setSelectedDistrict(null);
    }
  }, [selectedState]);

  // Handle change and pass data to parent
  const handleSelectionChange = () => {
    onSelectionChange({
      propertyTypes: selectedProperties.map((p) => p.value),
      state: selectedState ? selectedState.label : "",
      district: selectedDistrict ? selectedDistrict.label : "",
    });
  };

  useEffect(handleSelectionChange, [selectedProperties, selectedState, selectedDistrict]);

  // Reset Filters
  const resetFilters = () => {
    setSelectedProperties([]);
    setSelectedState(null);
    setSelectedDistrict(null);
  };

  return (
    <div className="mb-4 p-4 bg-white shadow-md rounded-lg relative">
      {/* Filters Heading + Reset Button */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-gray-800">Filters</h2>
        <button
          onClick={resetFilters}
          className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 text-sm rounded-lg"
        >
          Reset Filters
        </button>
      </div>

      {/* Property Type MultiSelect */}
      <div className="mb-4">
        <label className="block text-gray-600 font-medium mb-1">Property Type</label>
        <Select
          options={propertyOptions}
          value={selectedProperties}
          onChange={setSelectedProperties}
          isMulti
          placeholder="Select Property Type..."
          className="rounded-lg border-gray-300 shadow-sm"
        />
      </div>

      {/* State and District Select in One Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* State Dropdown */}
        <div>
          <label className="block text-gray-600 font-medium mb-1">State</label>
          <Select
            options={states}
            value={selectedState}
            onChange={(state) => {
              setSelectedState(state);
              setSelectedDistrict(null);
            }}
            placeholder="Select State..."
            className="rounded-lg border-gray-300 shadow-sm"
            isClearable={true}  // ✅ Cross Button Enabled
          />
        </div>

        {/* District Dropdown */}
        <div>
          <label className="block text-gray-600 font-medium mb-1">District & Cities</label>
          <Select
            options={districts}
            value={selectedDistrict}
            onChange={setSelectedDistrict}
            placeholder="Select District..."
            className="rounded-lg border-gray-300 shadow-sm"
            isDisabled={!selectedState}
            isClearable={true}  // ✅ Cross Button Enabled
          />
        </div>
      </div>
    </div>
  );
};

export default MultiSelectComponent;
